import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LayoutDashboard, Shirt, CalendarDays, Settings, LogOut, Loader2, Save, Plus, Trash2, Ruler, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { AdminLoadingState } from "../components/admin/AdminErrorBoundary";
import { AdminLayout } from "../components/admin/AdminSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function GuideEditor({ guide, onChange }) {
  const updateTable = (tIdx, field, value) => {
    const tables = [...guide.tables];
    tables[tIdx] = { ...tables[tIdx], [field]: value };
    onChange({ ...guide, tables });
  };

  const updateColumn = (tIdx, cIdx, value) => {
    const tables = [...guide.tables];
    const cols = [...tables[tIdx].columns];
    cols[cIdx] = value;
    tables[tIdx] = { ...tables[tIdx], columns: cols };
    onChange({ ...guide, tables });
  };

  const addColumn = (tIdx) => {
    const tables = [...guide.tables];
    tables[tIdx] = {
      ...tables[tIdx],
      columns: [...tables[tIdx].columns, "New Column"],
      rows: tables[tIdx].rows.map(r => [...r, ""])
    };
    onChange({ ...guide, tables });
  };

  const removeColumn = (tIdx, cIdx) => {
    const tables = [...guide.tables];
    tables[tIdx] = {
      ...tables[tIdx],
      columns: tables[tIdx].columns.filter((_, i) => i !== cIdx),
      rows: tables[tIdx].rows.map(r => r.filter((_, i) => i !== cIdx))
    };
    onChange({ ...guide, tables });
  };

  const updateCell = (tIdx, rIdx, cIdx, value) => {
    const tables = [...guide.tables];
    const rows = tables[tIdx].rows.map(r => [...r]);
    rows[rIdx][cIdx] = value;
    tables[tIdx] = { ...tables[tIdx], rows };
    onChange({ ...guide, tables });
  };

  const addRow = (tIdx) => {
    const tables = [...guide.tables];
    const emptyRow = tables[tIdx].columns.map(() => "");
    tables[tIdx] = { ...tables[tIdx], rows: [...tables[tIdx].rows, emptyRow] };
    onChange({ ...guide, tables });
  };

  const removeRow = (tIdx, rIdx) => {
    const tables = [...guide.tables];
    tables[tIdx] = { ...tables[tIdx], rows: tables[tIdx].rows.filter((_, i) => i !== rIdx) };
    onChange({ ...guide, tables });
  };

  const addTable = () => {
    onChange({
      ...guide,
      tables: [...guide.tables, { heading: "New Section", columns: ["Size", "Measurement"], rows: [["M", "0"]] }]
    });
  };

  const removeTable = (tIdx) => {
    onChange({ ...guide, tables: guide.tables.filter((_, i) => i !== tIdx) });
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">Guide Title</label>
        <Input value={guide.title} onChange={e => onChange({ ...guide, title: e.target.value })}
          className="border-[#282828]/10 text-[13px]" data-testid="size-guide-title-input" />
      </div>

      {/* Tables */}
      {guide.tables.map((table, tIdx) => (
        <div key={tIdx} className="bg-white border border-[#282828]/8 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 mr-4">
              <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">
                Section Heading {tIdx === 0 ? "(leave blank for main table)" : ""}
              </label>
              <Input value={table.heading} onChange={e => updateTable(tIdx, "heading", e.target.value)}
                placeholder="e.g. Lower Garments" className="border-[#282828]/10 text-[13px]" />
            </div>
            {guide.tables.length > 1 && (
              <button onClick={() => removeTable(tIdx)} className="text-[#676664]/30 hover:text-red-500 mt-4" title="Remove section">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Columns */}
          <div>
            <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">Column Headings</label>
            <div className="flex flex-wrap gap-2 items-center">
              {table.columns.map((col, cIdx) => (
                <div key={cIdx} className="flex items-center gap-1">
                  <Input value={col} onChange={e => updateColumn(tIdx, cIdx, e.target.value)}
                    className="w-32 border-[#282828]/10 text-[12px] h-8" />
                  {table.columns.length > 1 && (
                    <button onClick={() => removeColumn(tIdx, cIdx)} className="text-[#676664]/20 hover:text-red-400">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={() => addColumn(tIdx)} className="text-[10px] text-[#9A7B4F] hover:underline flex items-center gap-0.5">
                <Plus className="w-3 h-3" /> Column
              </button>
            </div>
          </div>

          {/* Rows */}
          <div>
            <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">Data Rows</label>
            <div className="space-y-1">
              {table.rows.map((row, rIdx) => (
                <div key={rIdx} className="flex items-center gap-1">
                  {row.map((cell, cIdx) => (
                    <Input key={cIdx} value={cell} onChange={e => updateCell(tIdx, rIdx, cIdx, e.target.value)}
                      className={`border-[#282828]/10 text-[11px] h-8 ${cIdx === 0 ? 'w-16 font-semibold' : 'w-28'}`} />
                  ))}
                  <button onClick={() => removeRow(tIdx, rIdx)} className="text-[#676664]/20 hover:text-red-400 flex-shrink-0">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => addRow(tIdx)} className="text-[10px] text-[#9A7B4F] hover:underline flex items-center gap-0.5 mt-2">
              <Plus className="w-3 h-3" /> Add Row
            </button>
          </div>
        </div>
      ))}

      <button onClick={addTable} className="text-[11px] text-[#9A7B4F] hover:underline flex items-center gap-1">
        <Plus className="w-3.5 h-3.5" /> Add New Section
      </button>

      {/* Note */}
      <div>
        <label className="text-[10px] text-[#676664]/60 uppercase block mb-1">Footer Note</label>
        <textarea value={guide.note} onChange={e => onChange({ ...guide, note: e.target.value })}
          rows={3} className="w-full border border-[#282828]/10 px-3 py-2 text-[12px] focus:border-[#9A7B4F] focus:outline-none resize-none rounded"
          data-testid="size-guide-note-input" />
      </div>
    </div>
  );
}

export default function AdminSizeGuide() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [guides, setGuides] = useState({});
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("general");
  const [showAddModal, setShowAddModal] = useState(false);
  const [dirty, setDirty] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) { navigate("/admin/login"); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => loadData(token))
      .catch(() => { localStorage.removeItem("adminToken"); navigate("/admin/login"); });
  }, [navigate]);

  const loadData = async (token) => {
    try {
      const [guidesRes, catsRes] = await Promise.all([
        axios.get(`${API}/settings/size-guides/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings/categories`)
      ]);
      setGuides(guidesRes.data.guides || {});
      setCategories(catsRes.data.categories || []);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const handleSave = async (categoryId) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(`${API}/settings/size-guide`,
        { guide: guides[categoryId], category_id: categoryId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDirty(prev => ({ ...prev, [categoryId]: false }));
      toast.success(`Size guide saved${categoryId !== 'general' ? ` for ${getCategoryName(categoryId)}` : ''}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" && detail
        ? `Failed to save: ${detail}`
        : (err?.message ? `Failed to save: ${err.message}` : "Failed to save");
      toast.error(msg);
    }
    finally { setSaving(false); }
  };

  const handleDelete = async (categoryId) => {
    if (categoryId === "general") return;
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/settings/size-guide/${categoryId}`, { headers: { Authorization: `Bearer ${token}` } });
      setGuides(prev => { const g = { ...prev }; delete g[categoryId]; return g; });
      setActiveTab("general");
      toast.success(`Size guide for ${getCategoryName(categoryId)} deleted`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" && detail ? `Failed to delete: ${detail}` : "Failed to delete");
    }
  };

  const handleAddGuide = (categoryId) => {
    setGuides(prev => ({
      ...prev,
      [categoryId]: {
        title: `${getCategoryName(categoryId)} Size Guide`,
        tables: [{ heading: "", columns: ["Size", "Measurement (cm)"], rows: [["S", ""], ["M", ""], ["L", ""]] }],
        note: "Please contact us if you need help with sizing."
      }
    }));
    setDirty(prev => ({ ...prev, [categoryId]: true }));
    setActiveTab(categoryId);
    setShowAddModal(false);
  };

  const updateGuide = (categoryId, guide) => {
    setGuides(prev => ({ ...prev, [categoryId]: guide }));
    setDirty(prev => ({ ...prev, [categoryId]: true }));
  };

  const getCategoryName = (key) => {
    if (key === "general") return "General";
    return (categories || []).find(c => c.id === key)?.name || key;
  };

  const guideKeys = Object.keys(guides);
  const unusedCategories = (categories || []).filter(c => !guides[c.id]);

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
    { icon: Shirt, label: "Catalogue", path: "/admin/catalogue" },
    { icon: CalendarDays, label: "Bookings", path: "/admin/bookings" },
    { icon: Settings, label: "Settings", path: "/admin/settings" },
  ];

  if (loading) return (
    <AdminLayout><AdminLoadingState message="Loading size guide..." /></AdminLayout>
  );

  return (
    <div className="min-h-screen bg-[#FAF8F0] flex">
      {/* Sidebar */}
      <aside className="w-56 bg-white border-r border-[#282828]/8 flex flex-col">
        <div className="px-5 py-6 border-b border-[#282828]/8">
          <h1 className="text-sm text-[#282828] tracking-[0.2em] uppercase">Mahra Admin</h1>
        </div>
        <nav className="flex-1 py-4">
          {navItems.map(item => (
            <button key={item.path} onClick={() => navigate(item.path)}
              className="w-full flex items-center gap-3 px-5 py-2.5 text-[12px] text-[#676664] hover:bg-[#9A7B4F]/5 hover:text-[#9A7B4F] transition-colors">
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-[#282828]/8">
          <button onClick={() => { localStorage.removeItem("adminToken"); navigate("/admin/login"); }}
            className="flex items-center gap-2 text-[11px] text-[#676664] hover:text-red-500">
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <div className="px-8 py-6 border-b border-[#282828]/8 bg-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Ruler className="w-5 h-5 text-[#9A7B4F]" />
            <div>
              <h2 className="text-base text-[#282828] font-light">Size Guide Editor</h2>
              <p className="text-[11px] text-[#676664]/50 mt-0.5">Manage size guides per category. Products show their category's guide automatically.</p>
            </div>
          </div>
          <div className="flex gap-2">
            {unusedCategories.length > 0 && (
              <Button variant="outline" onClick={() => setShowAddModal(true)} className="text-[11px] border-[#9A7B4F]/30 text-[#9A7B4F]" data-testid="add-category-guide-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Category Guide
              </Button>
            )}
            <Button onClick={() => handleSave(activeTab)} disabled={saving} className="bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white text-[11px]" data-testid="save-size-guide">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save {getCategoryName(activeTab)}
            </Button>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="px-8 bg-white border-b border-[#282828]/8">
          <div className="flex gap-0.5 overflow-x-auto">
            {guideKeys.map(key => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-5 py-3 text-[11px] uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
                  activeTab === key
                    ? 'border-[#9A7B4F] text-[#9A7B4F] font-medium'
                    : 'border-transparent text-[#676664]/50 hover:text-[#676664]'
                }`}
                data-testid={`tab-${key}`}
              >
                {getCategoryName(key)}
                {dirty[key] && <span className="w-1.5 h-1.5 bg-[#9A7B4F] rounded-full" />}
              </button>
            ))}
          </div>
        </div>

        {/* Editor */}
        <div className="px-8 py-6 max-w-4xl">
          {/* Category label + delete */}
          {activeTab !== "general" && (
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#282828]/5">
              <p className="text-[12px] text-[#676664]">
                This guide will show on all <span className="font-medium text-[#282828]">{getCategoryName(activeTab)}</span> product pages
              </p>
              <button onClick={() => handleDelete(activeTab)} className="text-[11px] text-red-400 hover:text-red-600 flex items-center gap-1" data-testid="delete-guide-btn">
                <Trash2 className="w-3 h-3" /> Delete this guide
              </button>
            </div>
          )}
          {activeTab === "general" && (
            <p className="text-[12px] text-[#676664] mb-4 pb-3 border-b border-[#282828]/5">
              This is the <span className="font-medium text-[#282828]">default guide</span> shown when a product's category doesn't have its own size guide.
            </p>
          )}

          {guides[activeTab] && (
            <GuideEditor guide={guides[activeTab]} onChange={(g) => updateGuide(activeTab, g)} />
          )}
        </div>
      </main>

      {/* Add Category Modal */}
      {showAddModal && (
        <>
          <div className="fixed inset-0 bg-black/30 z-50" onClick={() => setShowAddModal(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-lg shadow-xl w-[95%] max-w-md p-6" data-testid="add-guide-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[14px] text-[#282828]">Create Size Guide for Category</h3>
              <button onClick={() => setShowAddModal(false)} className="text-[#676664]/40 hover:text-[#676664]"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2">
              {unusedCategories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => handleAddGuide(cat.id)}
                  className="w-full text-left px-4 py-3 border border-[#282828]/8 hover:border-[#9A7B4F] hover:bg-[#9A7B4F]/5 transition-colors text-[13px] text-[#282828] flex items-center justify-between group"
                  data-testid={`add-guide-${cat.id}`}
                >
                  {cat.name}
                  <Plus className="w-4 h-4 text-[#676664]/20 group-hover:text-[#9A7B4F]" />
                </button>
              ))}
              {unusedCategories.length === 0 && (
                <p className="text-[12px] text-[#676664]/50 text-center py-4">All categories already have size guides.</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
